import React, { useState } from 'react';
import Header from '../components/Layout/Header';
import {
  BookOpen, ChevronDown, ChevronRight, Copy, Check,
  Zap, FileText, Mic, Settings, Globe
} from 'lucide-react';

interface Endpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'WS';
  path: string;
  summary: string;
  description: string;
  auth?: boolean;
  body?: Record<string, string>;
  params?: Record<string, string>;
  query?: Record<string, string>;
  headers?: Record<string, string>;
  response?: Record<string, string>;
}

interface ApiSection {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  baseUrl: string;
  color: string;
  endpoints: Endpoint[];
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  POST: 'bg-blue-100 text-blue-700 border-blue-200',
  PUT: 'bg-amber-100 text-amber-700 border-amber-200',
  DELETE: 'bg-red-100 text-red-700 border-red-200',
  PATCH: 'bg-purple-100 text-purple-700 border-purple-200',
  WS: 'bg-pink-100 text-pink-700 border-pink-200',
};

const API_SECTIONS: ApiSection[] = [
  {
    id: 'developer-api',
    title: 'Developer API — Chat & Query',
    description: 'Send legal queries to AI agents and get responses. Requires API key authentication.',
    icon: <Zap size={20} />,
    baseUrl: 'https://vidhiguruapi.vidhiguru.in',
    color: 'from-blue-500 to-cyan-500',
    endpoints: [
      {
        method: 'GET',
        path: '/api/v1/agents',
        summary: 'List Your Agents',
        description: 'Returns AI agents assigned to your account with their capabilities and available endpoints.',
        headers: { 'x-api-key': 'YOUR_API_KEY' },
      },
      {
        method: 'POST',
        path: '/api/v1/agents/:agentId/chat',
        summary: 'Chat (Non-Streaming)',
        description: 'Send a legal query and receive a complete response. Best for simple integrations.',
        body: {
          'query (required)': 'string — The legal question or prompt',
          'userId': 'string — Optional user identifier for session tracking',
          'requestId': 'string — Optional deduplication key (prevents double-billing)',
        },
        headers: { 'x-api-key': 'YOUR_API_KEY', 'Content-Type': 'application/json' },
        response: {
          'success': 'boolean',
          'response': 'string — The AI response text',
          'agentId': 'string — Agent that processed the query',
          'billing.tokensCharged': 'number — Tokens consumed',
          'billing.costINR': 'number — Cost in ₹',
          'billing.tokensRemaining': 'number — Remaining balance',
        },
      },
      {
        method: 'POST',
        path: '/api/v1/agents/:agentId/chat/stream',
        summary: 'Chat (Streaming — SSE)',
        description: 'Stream the AI response token-by-token via Server-Sent Events. Best for real-time chat UIs.',
        body: {
          'query (required)': 'string — The legal question or prompt',
          'userId': 'string — Optional user identifier',
        },
        headers: { 'x-api-key': 'YOUR_API_KEY', 'Content-Type': 'application/json' },
        response: {
          'event: token': 'data: {"text": "partial response..."} — Streamed chunks',
          'event: done': 'data: {"tokensCharged": 0.5, "response": "full text"} — Final event',
        },
      },
      {
        method: 'POST',
        path: '/api/v1/agents/:agentId/chat/pro',
        summary: 'Pro Chat (Non-Streaming)',
        description: 'Enhanced response using the Pro model. Use for complex legal analysis, drafting, and detailed research.',
        body: {
          'query (required)': 'string — Complex legal question',
        },
        headers: { 'x-api-key': 'YOUR_API_KEY', 'Content-Type': 'application/json' },
      },
      {
        method: 'POST',
        path: '/api/v1/agents/:agentId/chat/pro/stream',
        summary: 'Pro Chat (Streaming — SSE)',
        description: 'Stream Pro model response. Higher quality but slightly slower.',
        body: {
          'query (required)': 'string — Complex legal question',
        },
        headers: { 'x-api-key': 'YOUR_API_KEY', 'Content-Type': 'application/json' },
      },
    ],
  },
  {
    id: 'ocr-api',
    title: 'Document OCR',
    description: 'Extract text from images of legal documents (court orders, FIRs, agreements). Powered by advanced vision AI.',
    icon: <FileText size={20} />,
    baseUrl: 'https://vidhiguruapi.vidhiguru.in',
    color: 'from-emerald-500 to-teal-500',
    endpoints: [
      {
        method: 'POST',
        path: '/api/ocr/extract',
        summary: 'Extract Text from Image',
        description: 'Send a base64-encoded image and receive extracted text. Works with printed text, handwritten content, and degraded photocopies.',
        body: {
          'image (required)': 'string — Base64-encoded image data',
          'mimeType': 'string — image/jpeg, image/png, or image/webp (default: image/jpeg)',
        },
        headers: { 'x-api-key': 'YOUR_API_KEY', 'Content-Type': 'application/json' },
        response: {
          'success': 'boolean',
          'text': 'string — Extracted text content',
          'method': 'string — OCR method used',
          'wordCount': 'number — Word count of extracted text',
        },
      },
    ],
  },
  {
    id: 'docai-api',
    title: 'DocAI — Multimodal Document Query',
    description: 'Send files (images/PDFs) plus a text question in a single call. The model reads the files natively (vision) and answers. Charged like chat plus a flat +1 token surcharge per request.',
    icon: <FileText size={20} />,
    baseUrl: 'https://vidhiguruapi.vidhiguru.in',
    color: 'from-sky-500 to-blue-600',
    endpoints: [
      {
        method: 'POST',
        path: '/api/query/docai',
        summary: 'DocAI Query (Non-Streaming)',
        description: 'Send a text query plus one or more base64-encoded files. The model reads all files together and returns one answer. Accepted types: JPEG, PNG, WebP, GIF, PDF (max 50MB total).',
        body: {
          'query (required)': 'string — The question to ask about the files',
          'files (required)': 'array — [{ mimeType, data (base64), name }] — one or more files',
          'userId': 'string — Optional user identifier for session tracking',
          'agentId': 'string — Optional agent whose system prompt guides the answer',
          'systemPrompt': 'string — Optional explicit system instructions (overrides agent)',
        },
        headers: { 'x-api-key': 'YOUR_API_KEY', 'Content-Type': 'application/json' },
        response: {
          'success': 'boolean',
          'response': 'string — The AI answer based on the files',
          'model': 'string — Model identifier used for the response',
          'filesProcessed': 'number — How many files were read',
          'billing.tokensCharged': 'number — Tokens consumed (includes +1 DocAI surcharge)',
          'billing.costINR': 'number — Cost in ₹',
          'billing.tokensRemaining': 'number — Remaining balance',
        },
      },
      {
        method: 'POST',
        path: '/api/query/docai/stream',
        summary: 'DocAI Query (Streaming — SSE)',
        description: 'Same input as the non-streaming endpoint; the answer streams back as Server-Sent Events. Best for real-time UIs.',
        body: {
          'query (required)': 'string — The question to ask about the files',
          'files (required)': 'array — [{ mimeType, data (base64), name }] — one or more files',
          'userId': 'string — Optional user identifier',
          'agentId': 'string — Optional agent whose system prompt guides the answer',
        },
        headers: { 'x-api-key': 'YOUR_API_KEY', 'Content-Type': 'application/json' },
        response: {
          'event: chunk': 'data: {"text": "partial answer..."} — Streamed chunks',
          'event: done': 'data: {"tokensCharged": 1.5, "tokensRemaining": 998} — Final event',
          'event: error': 'data: {"error": "message"} — On failure',
        },
      },
    ],
  },
  {
    id: 'voice-api',
    title: 'Voice AI',
    description: 'Real-time voice conversation with AI agents via WebSocket.',
    icon: <Mic size={20} />,
    baseUrl: 'wss://vidhiguruapi.vidhiguru.in',
    color: 'from-purple-500 to-pink-500',
    endpoints: [
      {
        method: 'WS',
        path: '/live-voice',
        summary: 'Live Voice WebSocket',
        description: 'Bidirectional audio streaming. Send a setup message with your agentId, then stream PCM 16kHz audio chunks. AI responds with audio + text.',
        body: {
          'Setup message': '{ "type": "setup", "agentId": "junior", "config": { "language": "en" } }',
          'Audio message': '{ "type": "audio", "audio": "base64_pcm_data" }',
          'End signal': '{ "type": "audioStreamEnd" }',
        },
        response: {
          'Ready': '{ "type": "ready", "sessionId": "..." }',
          'Response': '{ "type": "response", "text": "...", "audio": "base64_audio" }',
          'Error': '{ "type": "error", "message": "..." }',
        },
      },
    ],
  },
  {
    id: 'utility',
    title: 'Utility Endpoints',
    description: 'Health check and pricing information. No authentication required.',
    icon: <Settings size={20} />,
    baseUrl: 'https://vidhiguruapi.vidhiguru.in',
    color: 'from-gray-500 to-slate-500',
    endpoints: [
      {
        method: 'GET',
        path: '/health',
        summary: 'Health Check',
        description: 'Check if the API service is running.',
      },
      {
        method: 'GET',
        path: '/api/pricing',
        summary: 'Pricing Information',
        description: 'Get current token pricing and billing details.',
      },
      {
        method: 'GET',
        path: '/api/docs',
        summary: 'API Documentation (JSON)',
        description: 'Machine-readable API documentation with all endpoints and examples.',
      },
    ],
  },
];
const EndpointCard: React.FC<{ endpoint: Endpoint; baseUrl: string }> = ({ endpoint, baseUrl }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const fullUrl = `${baseUrl}${endpoint.path}`;

  const copyCurl = () => {
    let curl = '';
    if (endpoint.method === 'GET') {
      curl = `curl -s ${fullUrl}`;
    } else if (endpoint.method === 'WS') {
      curl = `wscat -c ${fullUrl.replace('http', 'ws')}`;
    } else {
      const bodyExample = endpoint.body
        ? JSON.stringify(
            Object.fromEntries(
              Object.entries(endpoint.body).map(([k, v]) => [
                k.replace(/ \(required\)/, ''),
                v.includes('string') ? '' : v.includes('boolean') ? true : v.includes('integer') ? 0 : {},
              ])
            ),
            null,
            2
          )
        : '{}';
      curl = `curl -s -X ${endpoint.method} ${fullUrl} \\\n  -H "Content-Type: application/json" \\\n  -d '${bodyExample}'`;
    }
    if (endpoint.auth) {
      curl = curl.replace('\n', ` \\\n  -H "Authorization: Bearer <JWT_TOKEN>" \\\n`);
    }
    navigator.clipboard.writeText(curl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="border border-gray-200 rounded-lg hover:border-gray-300 transition-all">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 text-left"
      >
        <span className={`px-2.5 py-1 rounded text-xs font-bold border ${METHOD_COLORS[endpoint.method]}`}>
          {endpoint.method}
        </span>
        <code className="text-sm font-mono text-gray-700 flex-1">{endpoint.path}</code>
        {endpoint.auth && (
          <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700 border border-yellow-200">
            🔒 Auth
          </span>
        )}
        <span className="text-sm text-gray-500 hidden md:block max-w-xs truncate">{endpoint.summary}</span>
        {expanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
          <p className="text-sm text-gray-600">{endpoint.description}</p>

          {endpoint.params && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Path Parameters</h4>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                {Object.entries(endpoint.params).map(([key, desc]) => (
                  <div key={key} className="flex gap-2 text-sm">
                    <code className="text-purple-600 font-mono">{key}</code>
                    <span className="text-gray-500">—</span>
                    <span className="text-gray-600">{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {endpoint.query && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Query Parameters</h4>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                {Object.entries(endpoint.query).map(([key, desc]) => (
                  <div key={key} className="flex gap-2 text-sm">
                    <code className="text-blue-600 font-mono">{key}</code>
                    <span className="text-gray-500">—</span>
                    <span className="text-gray-600">{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {endpoint.body && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Request Body</h4>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                {Object.entries(endpoint.body).map(([key, desc]) => (
                  <div key={key} className="flex gap-2 text-sm">
                    <code className={`font-mono ${key.includes('required') ? 'text-red-600' : 'text-blue-600'}`}>
                      {key}
                    </code>
                    <span className="text-gray-500">—</span>
                    <span className="text-gray-600">{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 flex-1 overflow-x-auto">
              {endpoint.method === 'WS' ? fullUrl.replace('http', 'ws') : fullUrl}
            </code>
            <button
              onClick={copyCurl}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors"
              title="Copy cURL command"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied' : 'cURL'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ApiDocsPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSections = API_SECTIONS.map((section) => ({
    ...section,
    endpoints: section.endpoints.filter(
      (ep) =>
        !searchQuery ||
        ep.path.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ep.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ep.description.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((section) => section.endpoints.length > 0);

  const totalEndpoints = API_SECTIONS.reduce((sum, s) => sum + s.endpoints.length, 0);

  return (
    <div className="flex flex-col h-full">
      <Header title="API Documentation" subtitle="Complete endpoint reference for all JoiLaw services" />

      <div className="flex-1 overflow-y-auto">
        {/* Hero */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white px-8 py-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-white/10 rounded-lg">
                <Globe size={24} />
              </div>
              <div>
                <h2 className="text-xl font-semibold">JoiLaw API Reference</h2>
                <p className="text-gray-400 text-sm">
                  {API_SECTIONS.length} sections · {totalEndpoints} endpoints · 2 services
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
              <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                <div className="text-xs text-gray-400">CoreAPI</div>
                <div className="text-sm font-mono mt-1">vidhiguruapi.vidhiguru.in</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                <div className="text-xs text-gray-400">BillingAPI</div>
                <div className="text-sm font-mono mt-1">vidhiguruapi.vidhiguru.in</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                <div className="text-xs text-gray-400">Auth</div>
                <div className="text-sm font-mono mt-1">Bearer JWT</div>
              </div>
            </div>

            {/* Search */}
            <div className="mt-5">
              <input
                type="text"
                placeholder="Search endpoints by path, name, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-white/30"
              />
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="max-w-5xl mx-auto px-8 py-6 space-y-6 pb-20">
          {filteredSections.map((section) => {
            const isOpen = activeSection === section.id || searchQuery.length > 0;
            return (
              <div key={section.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <button
                  onClick={() => setActiveSection(isOpen && !searchQuery ? null : section.id)}
                  className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className={`p-2.5 rounded-lg bg-gradient-to-br ${section.color} text-white`}>
                    {section.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{section.title}</h3>
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                        {section.endpoints.length} endpoints
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">{section.description}</p>
                  </div>
                  <div className="text-xs font-mono text-gray-400 hidden md:block">{section.baseUrl}</div>
                  {isOpen ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 p-4 space-y-2">
                    {section.endpoints.map((ep, i) => (
                      <EndpointCard key={i} endpoint={ep} baseUrl={section.baseUrl} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {filteredSections.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <BookOpen size={40} className="mx-auto mb-3 opacity-50" />
              <p>No endpoints match "{searchQuery}"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiDocsPage;
